import { mxn } from '../../formato';

/**
 * Detalle propio de los proveedores de compra e importación (arquitectura 2.15, MVP3 E10):
 * lo que pide la solicitud y lo que trae la oferta de un broker, un agente aduanal (valor en
 * aduana), un transportista o un inspector. Las demás especialidades no tienen detalle.
 */
export const ESPECIALIDADES_COMPRA = ['broker', 'agente_aduanal', 'transportista', 'inspector'];
export const esDeCompra = (especialidad: string | null | undefined) =>
  ESPECIALIDADES_COMPRA.includes(especialidad ?? '');

type Detalle = Record<string, unknown>;
const t = (f: FormData, k: string) => String(f.get(k) ?? '').trim();
const num = (f: FormData, k: string) => (t(f, k) ? Number(t(f, k)) : undefined);

/** Campos de la solicitud según la especialidad; `vehiculo` llena el auto para el agente aduanal. */
export function DetalleSolicitudCompra({
  especialidad,
  vehiculo,
}: {
  especialidad: string;
  vehiculo?: {
    anio: number | null;
    marca: string | null;
    modelo: string | null;
    version?: string | null;
  } | null;
}) {
  if (especialidad === 'agente_aduanal')
    return (
      <>
        <label className="campo">
          <span>Año</span>
          <input
            name="d_anio"
            type="number"
            required
            min={1900}
            max={2100}
            defaultValue={vehiculo?.anio ?? ''}
          />
        </label>
        <label className="campo">
          <span>Marca</span>
          <input name="d_marca" required maxLength={40} defaultValue={vehiculo?.marca ?? ''} />
        </label>
        <label className="campo">
          <span>Modelo</span>
          <input name="d_modelo" required maxLength={60} defaultValue={vehiculo?.modelo ?? ''} />
        </label>
        <label className="campo">
          <span>Versión</span>
          <input name="d_version" maxLength={80} defaultValue={vehiculo?.version ?? ''} />
        </label>
        <label className="campo">
          <span>Vía de importación</span>
          <select name="d_tipoImportacion" defaultValue="definitiva_resto">
            <option value="definitiva_frontera">Definitiva en región fronteriza</option>
            <option value="definitiva_resto">Definitiva al resto del país</option>
            <option value="clasico">Clásico (30 años o más)</option>
            <option value="regimen_general">Régimen general al resto del país</option>
            <option value="regimen_general_frontera">Régimen general en región fronteriza</option>
          </select>
        </label>
        <label className="campo">
          <span>Región</span>
          <select name="d_region" defaultValue="resto">
            <option value="frontera">Región fronteriza</option>
            <option value="resto">Resto del país</option>
          </select>
        </label>
        <label className="campo">
          <span>Aduana de entrada</span>
          <input name="d_aduanaEntrada" maxLength={60} placeholder="Nuevo Laredo" />
        </label>
      </>
    );
  if (especialidad === 'broker')
    return (
      <>
        <label className="campo">
          <span>Subastas (copart, iaa, otra)</span>
          <input name="d_subastas" defaultValue="copart" />
        </label>
        <label className="campo">
          <span>Estados de EE. UU. (TX, CA…)</span>
          <input name="d_estadosEeuu" placeholder="TX, NM" />
        </label>
        <label className="campo">
          <span>Lotes al mes (aprox.)</span>
          <input name="d_lotesPorMes" type="number" min={1} max={500} />
        </label>
      </>
    );
  if (especialidad === 'transportista')
    return (
      <>
        <label className="campo">
          <span>Origen</span>
          <input
            name="d_origen"
            required
            maxLength={120}
            placeholder="Patio de Copart en Houston, TX"
          />
        </label>
        <label className="campo">
          <span>Destino</span>
          <input name="d_destino" required maxLength={120} placeholder="Mi lote en Monterrey" />
        </label>
        <label className="campo">
          <span>Aduana de entrada</span>
          <input name="d_aduanaEntrada" maxLength={60} />
        </label>
        <label className="campo">
          <span>Retirar antes del</span>
          <input name="d_fechaRetiroLimite" type="date" />
        </label>
        <label className="campo" style={{ flexDirection: 'row', alignItems: 'center' }}>
          <input name="d_anda" type="checkbox" style={{ minHeight: 0 }} />
          <span>El auto anda (Run and Drive)</span>
        </label>
      </>
    );
  if (especialidad === 'inspector')
    return (
      <>
        <label className="campo">
          <span>Subasta</span>
          <select name="d_subasta" defaultValue="copart">
            <option value="copart">Copart</option>
            <option value="iaa">IAA</option>
            <option value="otra">Otra</option>
          </select>
        </label>
        <label className="campo">
          <span>Número de lote</span>
          <input name="d_numeroLote" required maxLength={40} />
        </label>
        <label className="campo">
          <span>Patio</span>
          <input name="d_patio" required maxLength={120} placeholder="Copart Houston" />
        </label>
        <label className="campo">
          <span>Fecha de subasta</span>
          <input name="d_fechaSubasta" type="date" />
        </label>
        <label className="campo" style={{ gridColumn: '1 / -1' }}>
          <span>Qué revisar (uno por renglón)</span>
          <textarea
            name="d_puntos"
            rows={2}
            placeholder={'Arranque y ruidos\nChasis y bolsas de aire'}
          />
        </label>
      </>
    );
  return null;
}

/** Lee del formulario el detalle de la solicitud según la especialidad. */
export function detalleSolicitudDe(especialidad: string, f: FormData): Detalle | undefined {
  const lista = (k: string, mayus = false) =>
    t(f, k)
      .split(/[,\s]+/)
      .filter(Boolean)
      .map((x) => (mayus ? x.toUpperCase() : x.toLowerCase()));
  if (especialidad === 'agente_aduanal')
    return {
      anio: num(f, 'd_anio'),
      marca: t(f, 'd_marca'),
      modelo: t(f, 'd_modelo'),
      ...(t(f, 'd_version') && { version: t(f, 'd_version') }),
      tipoImportacion: t(f, 'd_tipoImportacion'),
      region: t(f, 'd_region'),
      ...(t(f, 'd_aduanaEntrada') && { aduanaEntrada: t(f, 'd_aduanaEntrada') }),
    };
  if (especialidad === 'broker')
    return {
      subastas: lista('d_subastas'),
      estadosEeuu: lista('d_estadosEeuu', true),
      ...(num(f, 'd_lotesPorMes') !== undefined && { lotesPorMes: num(f, 'd_lotesPorMes') }),
    };
  if (especialidad === 'transportista')
    return {
      origen: t(f, 'd_origen'),
      destino: t(f, 'd_destino'),
      ...(t(f, 'd_aduanaEntrada') && { aduanaEntrada: t(f, 'd_aduanaEntrada') }),
      ...(t(f, 'd_fechaRetiroLimite') && { fechaRetiroLimite: t(f, 'd_fechaRetiroLimite') }),
      anda: f.get('d_anda') === 'on',
    };
  if (especialidad === 'inspector')
    return {
      subasta: t(f, 'd_subasta'),
      numeroLote: t(f, 'd_numeroLote'),
      patio: t(f, 'd_patio'),
      ...(t(f, 'd_fechaSubasta') && { fechaSubasta: t(f, 'd_fechaSubasta') }),
      puntos: t(f, 'd_puntos')
        .split('\n')
        .map((x) => x.trim())
        .filter(Boolean),
    };
  return undefined;
}

/** Campos de la oferta según la especialidad (Portal de Proveedores). */
export function DetalleOfertaCompra({ especialidad }: { especialidad: string }) {
  if (especialidad === 'agente_aduanal')
    return (
      <>
        <label className="campo">
          <span>Valor en aduana (MXN)</span>
          <input
            name="o_valorAduanaMxn"
            required
            inputMode="decimal"
            pattern="\d{1,12}(\.\d{1,2})?"
          />
        </label>
        <label className="campo">
          <span>IGI (MXN)</span>
          <input name="o_igiMxn" inputMode="decimal" />
        </label>
        <label className="campo">
          <span>DTA (MXN)</span>
          <input name="o_dtaMxn" inputMode="decimal" />
        </label>
        <label className="campo">
          <span>IVA (MXN)</span>
          <input name="o_ivaMxn" inputMode="decimal" />
        </label>
        <label className="campo">
          <span>Honorarios (MXN)</span>
          <input name="o_honorariosMxn" inputMode="decimal" />
        </label>
        <label className="campo">
          <span>Vigencia (días)</span>
          <input name="o_vigenciaDias" type="number" min={1} max={365} defaultValue={30} />
        </label>
        <label className="campo">
          <span>Aduana de entrada</span>
          <input name="o_aduanaEntrada" maxLength={60} />
        </label>
        <label className="campo" style={{ flexDirection: 'row', alignItems: 'center' }}>
          <input name="o_padronPropio" type="checkbox" defaultChecked style={{ minHeight: 0 }} />
          <span>Importo con mi padrón si el cliente no tiene</span>
        </label>
      </>
    );
  if (especialidad === 'broker')
    return (
      <>
        <label className="campo">
          <span>Fee</span>
          <select name="o_calculo" defaultValue="fijo">
            <option value="fijo">Fijo por auto (USD)</option>
            <option value="porcentaje">% del martillo</option>
          </select>
        </label>
        <label className="campo">
          <span>Monto o porcentaje</span>
          <input name="o_monto" required inputMode="decimal" />
        </label>
        <label className="campo">
          <span>Mínimo (USD, si es %)</span>
          <input name="o_minimo" inputMode="decimal" />
        </label>
        <label className="campo">
          <span>Depósito que pides (USD)</span>
          <input name="o_depositoUsd" inputMode="decimal" />
        </label>
        <label className="campo">
          <span>Subastas (copart, iaa, otra)</span>
          <input name="o_subastas" defaultValue="copart" />
        </label>
        <label className="campo" style={{ flexDirection: 'row', alignItems: 'center' }}>
          <input name="o_incluyeTraslado" type="checkbox" style={{ minHeight: 0 }} />
          <span>Incluye el traslado al patio</span>
        </label>
      </>
    );
  if (especialidad === 'transportista')
    return (
      <>
        <label className="campo">
          <span>Días de tránsito</span>
          <input name="o_diasTransito" type="number" required min={0} max={60} defaultValue={5} />
        </label>
        <label className="campo">
          <span>Tipo de unidad</span>
          <select name="o_tipoUnidad" defaultValue="">
            <option value="">—</option>
            <option value="plataforma">Plataforma</option>
            <option value="madrina">Madrina</option>
            <option value="grua">Grúa</option>
            <option value="manejado">Manejado</option>
          </select>
        </label>
        <label className="campo" style={{ flexDirection: 'row', alignItems: 'center' }}>
          <input name="o_incluyeCruce" type="checkbox" style={{ minHeight: 0 }} />
          <span>Incluye el cruce</span>
        </label>
        <label className="campo" style={{ flexDirection: 'row', alignItems: 'center' }}>
          <input name="o_incluyeSeguro" type="checkbox" style={{ minHeight: 0 }} />
          <span>Incluye seguro de carga</span>
        </label>
      </>
    );
  if (especialidad === 'inspector')
    return (
      <>
        <label className="campo">
          <span>Puedo ir el</span>
          <input name="o_fechaDisponible" type="date" required />
        </label>
        <label className="campo">
          <span>Fotos mínimas</span>
          <input name="o_fotosMinimas" type="number" min={1} max={500} defaultValue={40} />
        </label>
        <label className="campo" style={{ flexDirection: 'row', alignItems: 'center' }}>
          <input name="o_incluyeVideo" type="checkbox" defaultChecked style={{ minHeight: 0 }} />
          <span>Incluye video de arranque</span>
        </label>
        <label className="campo" style={{ flexDirection: 'row', alignItems: 'center' }}>
          <input name="o_incluyeDtc" type="checkbox" style={{ minHeight: 0 }} />
          <span>Incluye lectura de códigos DTC</span>
        </label>
      </>
    );
  return null;
}

export function detalleOfertaDe(especialidad: string, f: FormData): Detalle | undefined {
  if (especialidad === 'agente_aduanal') {
    const contrib = ['igiMxn', 'dtaMxn', 'ivaMxn'].every((k) => t(f, `o_${k}`));
    return {
      valorAduanaMxn: t(f, 'o_valorAduanaMxn'),
      ...(contrib && {
        contribuciones: {
          igiMxn: t(f, 'o_igiMxn'),
          dtaMxn: t(f, 'o_dtaMxn'),
          ivaMxn: t(f, 'o_ivaMxn'),
          honorariosMxn: t(f, 'o_honorariosMxn') || '0',
        },
      }),
      vigenciaDias: num(f, 'o_vigenciaDias') ?? 30,
      ...(t(f, 'o_aduanaEntrada') && { aduanaEntrada: t(f, 'o_aduanaEntrada') }),
      padronPropio: f.get('o_padronPropio') === 'on',
    };
  }
  if (especialidad === 'broker')
    return {
      fee:
        t(f, 'o_calculo') === 'porcentaje'
          ? {
              calculo: 'porcentaje',
              porcentaje: t(f, 'o_monto'),
              ...(t(f, 'o_minimo') && { minimo: t(f, 'o_minimo') }),
            }
          : { calculo: 'fijo', monto: t(f, 'o_monto') },
      ...(t(f, 'o_depositoUsd') && { depositoUsd: t(f, 'o_depositoUsd') }),
      subastas: t(f, 'o_subastas')
        .split(/[,\s]+/)
        .filter(Boolean)
        .map((x) => x.toLowerCase()),
      incluyeTraslado: f.get('o_incluyeTraslado') === 'on',
    };
  if (especialidad === 'transportista')
    return {
      diasTransito: num(f, 'o_diasTransito') ?? 0,
      incluyeCruce: f.get('o_incluyeCruce') === 'on',
      incluyeSeguro: f.get('o_incluyeSeguro') === 'on',
      ...(t(f, 'o_tipoUnidad') && { tipoUnidad: t(f, 'o_tipoUnidad') }),
    };
  if (especialidad === 'inspector')
    return {
      fechaDisponible: t(f, 'o_fechaDisponible'),
      incluyeVideo: f.get('o_incluyeVideo') === 'on',
      incluyeDtc: f.get('o_incluyeDtc') === 'on',
      fotosMinimas: num(f, 'o_fotosMinimas') ?? 40,
    };
  return undefined;
}

/** Resumen legible del detalle (solicitud u oferta) para listas y tablas. */
export function resumenDetalle(especialidad: string, d: Detalle | null | undefined): string {
  if (!d) return '';
  const s = (k: string) => (d[k] === undefined || d[k] === null ? '' : String(d[k]));
  if (especialidad === 'agente_aduanal')
    return [
      s('valorAduanaMxn') && `valor en aduana ${mxn(s('valorAduanaMxn'))}`,
      s('vigenciaDias') && `${s('vigenciaDias')} días`,
      s('anio') && `${s('anio')} ${s('marca')} ${s('modelo')}`,
      s('tipoImportacion'),
    ]
      .filter(Boolean)
      .join(' · ');
  if (especialidad === 'broker') {
    const fee = d['fee'] as
      { calculo: string; monto?: string; porcentaje?: string; minimo?: string } | undefined;
    return [
      fee &&
        (fee.calculo === 'fijo'
          ? `fee USD ${fee.monto}`
          : `fee ${fee.porcentaje} %${fee.minimo ? ` (mín. ${fee.minimo})` : ''}`),
      s('depositoUsd') && `depósito USD ${s('depositoUsd')}`,
      Array.isArray(d['subastas']) && (d['subastas'] as string[]).join(', '),
    ]
      .filter(Boolean)
      .join(' · ');
  }
  if (especialidad === 'transportista')
    return [
      s('origen') && `${s('origen')} → ${s('destino')}`,
      s('diasTransito') && `${s('diasTransito')} días`,
      d['incluyeCruce'] ? 'con cruce' : '',
    ]
      .filter(Boolean)
      .join(' · ');
  if (especialidad === 'inspector')
    return [
      s('patio'),
      s('numeroLote') && `lote ${s('numeroLote')}`,
      s('fechaDisponible') && `el ${s('fechaDisponible')}`,
    ]
      .filter(Boolean)
      .join(' · ');
  return '';
}
